/**
 * Trust-layer verification — two independent seams.
 *
 * Seam 1 — AP KMS seal: verify the JWS(ES256) over the ledger head using
 *   pure WebCrypto (no `jose` dependency). The sealPublicKey (SPKI PEM) is
 *   pinned in the ProofBundle and passed to verifySeal directly.
 *
 * Seam 2 — Attester WebAuthn assertion: verify the DER-encoded ECDSA
 *   signature over authData‖sha256(clientDataJSON) using WebCrypto, and
 *   confirm the challenge embedded in clientDataJSON equals the expected
 *   SHA-256 of the canonical attestation event JSON.
 *
 * Returns a typed VerifyResult rather than a bare boolean so the UI and
 * the offline verifier can surface actionable failure reasons.
 *
 * Canonical contract: gg-app CONTRACT.md (Lane B).
 * Chain integrity: entryHash = SHA-256(canonicalJSON(entry with entryHash="")).
 * Challenge: SHA-256(canonicalJSON({vehicleId, headHash, statement, createdAt})).
 * Canonical JSON = recursively key-sorted keys, UTF-8, no whitespace.
 * createdAt is hashed over the exact wire string (RFC 3339 with Z suffix).
 */

import type { Attestation, LedgerEntry, ProofBundle, Seal } from '@/types/domain';
import {
  canonicalJSON,
  computeAttestationChallenge,
  decodeBase64url,
  derEcdsaToRaw,
  importSpkiBase64ForVerify,
  importSpkiPemForVerify,
  parseClientDataJSON,
  sha256Hex,
  sha256Raw,
  validateAuthenticatorData,
  verifyJwsEs256,
} from './crypto';

export { computeAttestationChallenge } from './crypto';

// ---------------------------------------------------------------------------
// WebAuthn ceremony configuration
// ---------------------------------------------------------------------------

/**
 * Default allowed WebAuthn origins for assertion verification.
 *
 * These are DEV defaults only. Production callers (ProvenancePanel, the
 * offline-verifier exporter) must pass the env-resolved lists
 * (VITE_TRUST_ALLOWED_ORIGINS / VITE_TRUST_RP_ID) so verification matches
 * the deployment context. Post-Capacitor-wrap, these map to universal
 * link / app link origins.
 */
const ALLOWED_ORIGINS: string[] = [
  'http://localhost',
  'https://localhost',
  'https://localhost:5173',
  'http://localhost:5173',
  'https://absolutepinks.com',
];

/**
 * Default RP IDs for rpIdHash validation — dev defaults only; see above.
 */
const DEFAULT_RP_IDS: string[] = ['localhost', 'absolutepinks.com'];

// ---------------------------------------------------------------------------
// Result types
// ---------------------------------------------------------------------------

export type VerifyStatus = 'ok' | 'fail';

export interface IntegrityResult {
  status: VerifyStatus;
  failReason?: string;
  entries: EntryIntegrityResult[];
}

export interface EntryIntegrityResult {
  index: number;
  entryId: string;
  status: VerifyStatus;
  failReason?: string;
}

export interface SealResult {
  status: VerifyStatus;
  failReason?: string;
  verifiedHeadHash?: string;
}

export interface AttestationVerifyResult {
  status: VerifyStatus;
  failReason?: string;
  challengeMatch?: boolean;
  /**
   * The seq of the chain entry whose entryHash the attestation covers.
   * Equal to entries.length - 1 when the attestation is over the current
   * head; smaller when an expert attested an earlier ledger state (valid —
   * the chain commits to its prefix). Only set when status === 'ok'.
   */
  attestedSeq?: number;
}

export interface VerifyResult {
  integrity: IntegrityResult;
  seal: SealResult;
  attestation: AttestationVerifyResult | null;
  headMatches: { status: VerifyStatus; failReason?: string };
  vehicleIdMatches: { status: VerifyStatus; failReason?: string };
  allOk: boolean;
}

// ---------------------------------------------------------------------------
// Seam 1a — Chain integrity (re-derive entryHash from fields)
// ---------------------------------------------------------------------------

/**
 * Verify the hash-linked integrity of all ledger entries.
 *
 * For each entry i:
 *   - Recompute entryHash = SHA-256(canonicalJSON(entry with entryHash="")).
 *   - Compare recomputed hash to entry.entryHash.
 *   - Confirm seq is contiguous from 0.
 *   - Genesis (i=0): prevHash must be null.
 *   - Others: prevHash must equal entries[i-1].entryHash.
 */
export async function verifyChainIntegrity(entries: LedgerEntry[]): Promise<IntegrityResult> {
  if (entries.length === 0) {
    return { status: 'fail', failReason: 'no entries', entries: [] };
  }

  const entryResults: EntryIntegrityResult[] = [];
  let chainOk = true;

  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i];
    if (entry === undefined) {
      entryResults.push({
        index: i,
        entryId: `<unknown:${i}>`,
        status: 'fail',
        failReason: 'missing entry',
      });
      chainOk = false;
      continue;
    }

    // Verify seq is contiguous.
    if (entry.seq !== i) {
      entryResults.push({
        index: i,
        entryId: entry.id,
        status: 'fail',
        failReason: `seq ${entry.seq} does not match expected ${i}`,
      });
      chainOk = false;
      continue;
    }

    // Re-derive entryHash via canonicalJSON with entryHash set to "".
    const forHashing = { ...entry, entryHash: '' };
    const canonical = canonicalJSON(forHashing);
    const canonicalBytes = new TextEncoder().encode(canonical) as Uint8Array<ArrayBuffer>;
    const computedHash = await sha256Hex(canonicalBytes);

    if (computedHash !== entry.entryHash) {
      entryResults.push({
        index: i,
        entryId: entry.id,
        status: 'fail',
        failReason: `tamperedEntry: entryHash mismatch: computed ${computedHash}, stored ${entry.entryHash}`,
      });
      chainOk = false;
      continue;
    }

    // Verify prevHash linkage.
    if (i === 0) {
      if (entry.prevHash !== null) {
        entryResults.push({
          index: i,
          entryId: entry.id,
          status: 'fail',
          failReason: `forkDetected: genesis entry prevHash must be null, got ${entry.prevHash}`,
        });
        chainOk = false;
        continue;
      }
    } else {
      const prev = entries[i - 1];
      if (prev === undefined) {
        entryResults.push({
          index: i,
          entryId: entry.id,
          status: 'fail',
          failReason: 'internal: prev entry missing',
        });
        chainOk = false;
        continue;
      }
      if (entry.prevHash !== prev.entryHash) {
        entryResults.push({
          index: i,
          entryId: entry.id,
          status: 'fail',
          failReason: `forkDetected: prevHash ${entry.prevHash} does not match predecessor entryHash ${prev.entryHash}`,
        });
        chainOk = false;
        continue;
      }
    }

    entryResults.push({ index: i, entryId: entry.id, status: 'ok' });
  }

  return {
    status: chainOk ? 'ok' : 'fail',
    entries: entryResults,
  };
}

// ---------------------------------------------------------------------------
// Seam 1b — AP KMS seal (JWS ES256 via WebCrypto, no jose)
// ---------------------------------------------------------------------------

/**
 * Verify the AP KMS seal JWS(ES256) against the pinned seal public key.
 *
 * Uses pure WebCrypto SubtleCrypto — no `jose` library. The JWS signature
 * is raw r‖s (64 bytes) base64url; Cloud KMS emits DER which gg-app converts
 * to raw before assembling the JWS.
 *
 * @param seal - The Seal object from the ProofBundle.
 * @param sealPublicKey - SPKI PEM string from ProofBundle.sealPublicKey.
 * @param expectedHeadHash - The bundle's headHash to confirm the JWS payload.
 */
export async function verifySeal(
  seal: Seal,
  sealPublicKey: string,
  expectedHeadHash: string,
): Promise<SealResult> {
  let publicKey: CryptoKey;
  try {
    publicKey = await importSpkiPemForVerify(sealPublicKey);
  } catch (err) {
    return {
      status: 'fail',
      failReason: `forgedSeal: could not import seal public key: ${err instanceof Error ? err.message : String(err)}`,
    };
  }

  let payload: Record<string, unknown>;
  try {
    payload = await verifyJwsEs256(seal.jws, publicKey);
  } catch (err) {
    return {
      status: 'fail',
      failReason: `forgedSeal: ${err instanceof Error ? err.message : String(err)}`,
    };
  }

  const payloadHeadHash = payload['headHash'];
  if (typeof payloadHeadHash !== 'string') {
    return { status: 'fail', failReason: 'JWS payload missing headHash field' };
  }

  if (payloadHeadHash !== expectedHeadHash) {
    return {
      status: 'fail',
      failReason: `sealHeadMismatch: JWS headHash "${payloadHeadHash}" does not match bundle headHash "${expectedHeadHash}"`,
    };
  }

  return { status: 'ok', verifiedHeadHash: payloadHeadHash };
}

// ---------------------------------------------------------------------------
// Seam 2 — WebAuthn assertion verification
// ---------------------------------------------------------------------------

/**
 * Verify a single WebAuthn assertion for an attestation event.
 *
 * Algorithm per canonical contract:
 *   1. Decode authenticatorData, clientDataJSON, signature from base64url.
 *   2. Convert DER signature to raw r‖s (64 bytes).
 *   3. Validate authenticatorData: UV flag (0x04), UP flag (0x01), rpIdHash.
 *   4. Parse clientDataJSON: type == "webauthn.get", origin in allowlist.
 *   5. Extract and confirm the challenge matches
 *      SHA-256(canonicalJSON(attestation event)).
 *   6. Compute signed bytes: authenticatorData ‖ sha256(clientDataJSON).
 *   7. Verify ECDSA/P-256 signature over signed bytes with attesterPublicKey.
 *
 * @param attestation - The Attestation from the ProofBundle.
 * @param attesterPublicKey - SPKI DER base64 (standard) from ProofBundle.attesterPublicKey.
 * @param allowedOrigins - Origins to accept (default: ALLOWED_ORIGINS).
 * @param allowedRpIds - RP IDs to try for rpIdHash validation (default: DEFAULT_RP_IDS).
 */
export async function verifyAttestation(
  attestation: Attestation,
  attesterPublicKey: string,
  allowedOrigins: string[] = ALLOWED_ORIGINS,
  allowedRpIds: string[] = DEFAULT_RP_IDS,
): Promise<AttestationVerifyResult> {
  let authData: Uint8Array<ArrayBuffer>;
  let clientDataJSON: Uint8Array<ArrayBuffer>;
  let sigDer: Uint8Array<ArrayBuffer>;

  try {
    authData = decodeBase64url(attestation.authenticatorData);
    clientDataJSON = decodeBase64url(attestation.clientDataJSON);
    sigDer = decodeBase64url(attestation.signature);
  } catch (err) {
    return {
      status: 'fail',
      failReason: `base64url decode failed: ${err instanceof Error ? err.message : String(err)}`,
    };
  }

  // Convert DER signature to raw r‖s for WebCrypto.
  let sigRaw: Uint8Array<ArrayBuffer>;
  try {
    sigRaw = derEcdsaToRaw(sigDer);
  } catch (err) {
    return {
      status: 'fail',
      failReason: `DER signature decode failed: ${err instanceof Error ? err.message : String(err)}`,
    };
  }

  // Validate authenticatorData: UV + UP flags, rpIdHash.
  // Try each allowed RP ID; accept if any matches.
  let authDataOk = false;
  let authDataError = '';
  for (const rpId of allowedRpIds) {
    try {
      await validateAuthenticatorData(authData, rpId);
      authDataOk = true;
      break;
    } catch (err) {
      authDataError = err instanceof Error ? err.message : String(err);
    }
  }
  if (!authDataOk) {
    return {
      status: 'fail',
      failReason: authDataError || 'authenticatorData validation failed',
    };
  }

  // Parse and validate clientDataJSON.
  let embeddedChallenge: string;
  try {
    const parsed = parseClientDataJSON(clientDataJSON, allowedOrigins);
    embeddedChallenge = parsed.challenge;
  } catch (err) {
    return {
      status: 'fail',
      failReason: `clientDataJSON validation failed: ${err instanceof Error ? err.message : String(err)}`,
    };
  }

  // Verify the challenge.
  const expectedChallenge = await computeAttestationChallenge({
    vehicleId: attestation.vehicleId,
    headHash: attestation.headHash,
    statement: attestation.statement,
    createdAt: attestation.createdAt,
  });

  const challengeMatch = embeddedChallenge === expectedChallenge;
  if (!challengeMatch) {
    return {
      status: 'fail',
      failReason: `challengeMismatch: embedded "${embeddedChallenge}" !== expected "${expectedChallenge}"`,
      challengeMatch: false,
    };
  }

  // Import the attester's public key.
  let publicKey: CryptoKey;
  try {
    publicKey = await importSpkiBase64ForVerify(attesterPublicKey);
  } catch (err) {
    return {
      status: 'fail',
      failReason: `public key import failed: ${err instanceof Error ? err.message : String(err)}`,
      challengeMatch,
    };
  }

  // Compute signed bytes: authData ‖ sha256(clientDataJSON).
  const clientDataHash = await sha256Raw(clientDataJSON);
  const signedBuf = new ArrayBuffer(authData.length + 32);
  const signedData = new Uint8Array(signedBuf);
  signedData.set(authData, 0);
  signedData.set(new Uint8Array(clientDataHash), authData.length);

  let valid: boolean;
  try {
    valid = await crypto.subtle.verify(
      { name: 'ECDSA', hash: 'SHA-256' },
      publicKey,
      sigRaw.buffer,
      signedData.buffer,
    );
  } catch (err) {
    return {
      status: 'fail',
      failReason: `signature verification threw: ${err instanceof Error ? err.message : String(err)}`,
      challengeMatch,
    };
  }

  if (!valid) {
    return {
      status: 'fail',
      failReason: 'forgedAttestation: ECDSA signature is invalid',
      challengeMatch,
    };
  }

  return { status: 'ok', challengeMatch: true };
}

// ---------------------------------------------------------------------------
// Full bundle verification
// ---------------------------------------------------------------------------

export interface VerifyBundleOptions {
  /** The head hash the caller is viewing in the UI (from the dossier). */
  expectedHead?: string;
  /** The vehicle ID the caller is viewing in the UI. */
  expectedVehicleId?: string;
  /** Allowed WebAuthn origins (default: module-level ALLOWED_ORIGINS). */
  allowedOrigins?: string[];
  /** Allowed RP IDs for rpIdHash check (default: module-level DEFAULT_RP_IDS). */
  allowedRpIds?: string[];
}

/**
 * Verify a complete ProofBundle.
 *
 * Runs all checks (chain integrity, AP seal, optional attestation, head
 * consistency, vehicle ID consistency) and returns a VerifyResult where
 * each field is independently inspectable for actionable failure information.
 *
 * Online/offline: purely client-side WebCrypto — zero network calls.
 *
 * @param bundle - The ProofBundle to verify.
 * @param options - Optional context: expected head hash and vehicle ID from
 *   the UI — used to detect head-mismatch and vehicle-ID-mismatch attacks.
 */
export async function verifyProofBundle(
  bundle: ProofBundle,
  options: VerifyBundleOptions = {},
): Promise<VerifyResult> {
  const { expectedHead, expectedVehicleId, allowedOrigins, allowedRpIds } = options;

  // Run integrity and seal checks in parallel.
  const [integrity, seal] = await Promise.all([
    verifyChainIntegrity(bundle.entries),
    verifySeal(bundle.seal, bundle.sealPublicKey, bundle.headHash),
  ]);

  // Verify attestation if present.
  //
  // Binding to THIS bundle is checked here, not in verifyAttestation: the
  // assertion's challenge only commits to the attestation's OWN fields, so
  // without these cross-checks a genuine attestation lifted from another
  // vehicle (or an unrelated chain) would verify green when transplanted
  // into this bundle.
  //   - vehicleId must equal the bundle's vehicleId.
  //   - headHash must equal the entryHash of some entry in this chain.
  //     (Not necessarily the last: gg-app may return an attestation made
  //     before later entries were appended. entryHash commits to the whole
  //     prefix, so matching any chain entry proves the attested state is
  //     an ancestor of the current head.)
  let attestation: AttestationVerifyResult | null = null;
  if (bundle.attestation !== null) {
    const att = bundle.attestation;
    if (bundle.attesterPublicKey === null) {
      attestation = {
        status: 'fail',
        failReason: 'bundle has attestation but attesterPublicKey is null',
      };
    } else if (att.vehicleId !== bundle.vehicleId) {
      attestation = {
        status: 'fail',
        failReason: `attestationVehicleMismatch: attestation is for vehicle "${att.vehicleId}", bundle is for "${bundle.vehicleId}"`,
      };
    } else {
      const attestedEntry = bundle.entries.find((e) => e.entryHash === att.headHash);
      if (attestedEntry === undefined) {
        attestation = {
          status: 'fail',
          failReason: `attestationHeadUnknown: attestation headHash "${att.headHash}" does not match any entry in this chain`,
        };
      } else {
        attestation = await verifyAttestation(
          att,
          bundle.attesterPublicKey,
          allowedOrigins,
          allowedRpIds,
        );
        if (attestation.status === 'ok') {
          attestation = { ...attestation, attestedSeq: attestedEntry.seq };
        }
      }
    }
  }

  // Head consistency: bundle.headHash must match the last entry's entryHash.
  const lastEntry = bundle.entries[bundle.entries.length - 1];
  let headMatches: VerifyResult['headMatches'];
  if (lastEntry === undefined) {
    headMatches = { status: 'fail', failReason: 'headMismatch: bundle has no entries' };
  } else if (lastEntry.entryHash !== bundle.headHash) {
    headMatches = {
      status: 'fail',
      failReason: `headMismatch: bundle.headHash "${bundle.headHash}" does not match last entry entryHash "${lastEntry.entryHash}"`,
    };
  } else if (expectedHead !== undefined && bundle.headHash !== expectedHead) {
    headMatches = {
      status: 'fail',
      failReason: `headMismatch: bundle headHash "${bundle.headHash}" does not match expected head "${expectedHead}" from dossier`,
    };
  } else {
    headMatches = { status: 'ok' };
  }

  // Vehicle ID consistency.
  let vehicleIdMatches: VerifyResult['vehicleIdMatches'];
  if (expectedVehicleId !== undefined && bundle.vehicleId !== expectedVehicleId) {
    vehicleIdMatches = {
      status: 'fail',
      failReason: `vehicleIdMismatch: bundle vehicleId "${bundle.vehicleId}" does not match expected "${expectedVehicleId}"`,
    };
  } else {
    vehicleIdMatches = { status: 'ok' };
  }

  const allOk =
    integrity.status === 'ok' &&
    seal.status === 'ok' &&
    (attestation === null || attestation.status === 'ok') &&
    headMatches.status === 'ok' &&
    vehicleIdMatches.status === 'ok';

  return { integrity, seal, attestation, headMatches, vehicleIdMatches, allOk };
}
